import { inject, Injectable } from '@angular/core';
import { LoginApiService } from './login-api.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private codeVerifier = Math.random().toString(36).substring(2, 15);
  private codeChallenge = '';
  private csrfToken = '';

  private apiService = inject(LoginApiService);
  private router = inject(Router);

  public async startLoginFlow(): Promise<TLoginState> {
    // 1st step - create codeChallenge
    const buffer = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(this.codeVerifier)
    );
    this.codeChallenge = this.base64urlEncode(buffer);

    // 2st step - call auth, if it fails because of 401 as expected, start login flow
    try {
      const authorizedRes = await this.getAuthApiCall();
      if (!authorizedRes) {
        return await this.loginFlow();
      }
      return 'SUCCESS';
    } catch (error) {
      return await this.loginFlow();
    }
  }

  public async logout() {
    window.localStorage.removeItem('accessToken');
    this.router.navigate(['login']);
  }

  private async loginFlow(): Promise<TLoginState> {
    try {
      // 3rd step - delete possible previous CSRF token
      this.deleteAllCookies();
      // 4th step - get new CSRF token
      this.csrfToken = await this.getCSRFToken();
      // 5th step - fetch passkey opts
      const passKeyOpts = await this.getLoginOptions();
      // 6th step - get passkey on client
      const credentials = await this.processPasskey(passKeyOpts);

      // 7th step - if passkey not found, we need to display error message that user needs to register first
      if (!credentials) {
        return 'CREDENTIALS_NOT_FOUND';
      }

      // 8th step - finalize passkey opt call
      const loginFinishResp = await this.getLoginFinish(credentials);
      // 9th step - fetch auth code
      const authCode = await this.getAuthApiCall();
      // 10th step - delete all cookies as on beginning of the process
      this.deleteAllCookies();
      // 11th step - finaly get accessToken
      await this.getAuthToken(authCode);

      return 'SUCCESS';
    } catch (error) {
      return 'ERROR';
    }
  }

  private async getAuthToken(code: string): Promise<TLoginState> {
    const bearerTokenResp = await this.apiService.getAuthToken(
      code,
      this.codeVerifier
    );

    if (bearerTokenResp.access_token) {
      window.localStorage.setItem('accessToken', bearerTokenResp.access_token);
      return 'SUCCESS';
    } else {
      return 'ERROR';
    }
  }

  private deleteAllCookies() {
    document.cookie.split(';').forEach(function (cookie) {
      const name = cookie.split('=')[0].trim();
      document.cookie =
        name + '=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;';
    });
  }

  private getLoginFinish(credentials: Credential) {
    const assertion = credentials as PublicKeyCredential;
    const response = assertion.response as AuthenticatorAssertionResponse;

    const body = {
      id: assertion.id,
      rawId: this.base64urlEncode(assertion.rawId),
      type: assertion.type,
      authenticatorAttachment: 'platform',

      response: {
        authenticatorData: this.base64urlEncode(response.authenticatorData),
        clientDataJSON: this.base64urlEncode(response.clientDataJSON),
        signature: this.base64urlEncode(response.signature),
        userHandle: response.userHandle
          ? this.base64urlEncode(response.userHandle)
          : null,
      },
    };

    return this.apiService.getLoginFinish(body, this.csrfToken);
  }

  private async processPasskey(
    challengeResponse: any
  ): Promise<PublicKeyCredential | null> {
    const publicKey: PublicKeyCredentialRequestOptions = {
      challenge: Uint8Array.from(atob(challengeResponse.challenge.value), c =>
        c.charCodeAt(0)
      ),
      rpId: challengeResponse.rpId,
      timeout: challengeResponse.timeout,
      userVerification: challengeResponse.userVerification,
      ...(challengeResponse.allowCredentials && {
        allowCredentials: challengeResponse.allowCredentials,
      }),
    };

    try {
      const credential = await navigator.credentials.get({ publicKey });
      return credential as PublicKeyCredential;
    } catch (err) {
      console.error('WebAuthn login failed:', err);
      return null;
    }
  }

  private async getAuthApiCall(): Promise<string> {
    return this.apiService.getAuthApiCall(this.codeChallenge);
  }

  private async getCSRFToken() {
    return this.apiService.getCSRFToken();
  }

  private getLoginOptions() {
    return this.apiService.getLoginOptions(this.csrfToken);
  }

  private base64urlEncode(buffer: any) {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }
}

export type TLoginState =
  | 'SUCCESS'
  | 'CREDENTIALS_NOT_FOUND'
  | 'ERROR'
  | 'NOT_STARTED'
  | 'UNAUTHORIZED'
  | 'DISABLED'
  | 'IN_PROGRESS';
